// ========================================================
// FAST HIT Website — Hero Section
// Ultra-premium sports-tech hero with animated particles,
// real-time live match feed & glassmorphism overlay
// ========================================================

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  ShieldCheck,
  Zap,
  Trophy,
  TrendingUp,
  Star,
} from 'lucide-react';
import { subscribeLiveMatches } from '../../services/matchesService';
import type { Match } from '../../types';
import './HeroSection.css';

/** Floating animated particles for the hero background */
function HeroParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const particles: { x: number; y: number; vx: number; vy: number; r: number; o: number; color: string }[] = [];
    const colors = ['rgba(22,139,255,0.4)', 'rgba(40,215,255,0.3)', 'rgba(255,83,100,0.2)'];

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);

    // Create particles
    for (let i = 0; i < 45; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2.5 + 0.5,
        o: Math.random() * 0.5 + 0.1,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.offsetWidth) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.offsetHeight) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.o;
        ctx.fill();
      });

      // Draw connecting lines
      ctx.globalAlpha = 0.06;
      ctx.strokeStyle = 'rgba(40, 215, 255, 0.8)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          if (dx * dx + dy * dy < 12000) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero__particles" aria-hidden="true" />;
}

/** Real-time mini scorecard displayed in the hero */
function HeroLiveScorecard({ match }: { match: Match }) {
  return (
    <div className="hero__scorecard-card">
      {/* Header */}
      <div className="hero__scorecard-header">
        <div className="hero__live-pill">
          <span className="hero__pulse-dot hero__pulse-dot--red" />
          <span>LIVE • {match.format}{match.tournamentName ? ` • ${match.tournamentName}` : ''}</span>
        </div>
        <span className="hero__ground-label">{match.groundName}{match.city ? `, ${match.city}` : ''}</span>
      </div>

      {/* Teams & Scores */}
      <div className="hero__teams-grid">
        <div className="hero__team-row">
          <div className="hero__team-info">
            <div className="hero__team-avatar">{match.teamA.shortName?.slice(0, 2) || 'TA'}</div>
            <span className="hero__team-name">{match.teamA.name}</span>
          </div>
          <div className="hero__team-score-box">
            <span className="hero__team-runs">{match.teamA.score}</span>
            <span className="hero__team-overs">({match.teamA.overs} ov)</span>
          </div>
        </div>

        <div className="hero__team-row hero__team-row--target">
          <div className="hero__team-info">
            <div className="hero__team-avatar hero__team-avatar--alt">{match.teamB.shortName?.slice(0, 2) || 'TB'}</div>
            <span className="hero__team-name">{match.teamB.name}</span>
          </div>
          <div className="hero__team-score-box">
            <span className="hero__team-runs">{match.teamB.score}</span>
            <span className="hero__team-overs">({match.teamB.overs} ov)</span>
          </div>
        </div>
      </div>

      {/* Match Equation Banner */}
      {match.resultString && (
        <div className="hero__equation">
          <span>{match.resultString}</span>
        </div>
      )}

      {/* View Full Scorecard link */}
      <Link to={`/matches/${match.id}`} className="hero__scorecard-footer-cta">
        <span>Open Full Interactive Scorecard</span>
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

/** Static showcase card when no live matches */
function HeroShowcase() {
  return (
    <div className="hero__showcase">
      <div className="hero__showcase-inner">
        <div className="hero__showcase-glow" />

        {/* Floating stat cards */}
        <div className="hero__float-stat hero__float-stat--1">
          <Activity size={18} />
          <div>
            <span className="hero__float-label">Live Matches</span>
            <span className="hero__float-value">Stream in real-time</span>
          </div>
        </div>

        <div className="hero__float-stat hero__float-stat--2">
          <TrendingUp size={18} />
          <div>
            <span className="hero__float-label">Player Analytics</span>
            <span className="hero__float-value">Deep performance insights</span>
          </div>
        </div>

        <div className="hero__float-stat hero__float-stat--3">
          <Trophy size={18} />
          <div>
            <span className="hero__float-label">Tournament Hub</span>
            <span className="hero__float-value">Auto standings & NRR</span>
          </div>
        </div>

        <div className="hero__showcase-cricket-icon">
          <span className="hero__cricket-ball">🏏</span>
        </div>
      </div>
    </div>
  );
}

export function HeroSection() {
  const [liveMatches, setLiveMatches] = useState<Match[]>([]);

  useEffect(() => {
    const unsub = subscribeLiveMatches((matches) => {
      setLiveMatches(matches);
    }, 2);
    return unsub;
  }, []);

  const currentMatch = liveMatches[0];

  return (
    <section className="hero" aria-labelledby="hero-title">
      {/* Animated particle background */}
      <HeroParticles />

      {/* Dynamic Background Glows */}
      <div className="hero__glow hero__glow--primary" aria-hidden="true" />
      <div className="hero__glow hero__glow--cyan" aria-hidden="true" />
      <div className="hero__glow hero__glow--accent" aria-hidden="true" />

      <div className="hero__container">
        {/* Left Column: Headlines & Actions */}
        <div className="hero__content">
          <div className="hero__tagline-badge">
            <span className="hero__pulse-dot" />
            <span>Real-Time Cricket Network</span>
          </div>

          <h1 id="hero-title" className="hero__title">
            Every Ball.<br />
            Every Match.<br />
            <span className="text-gradient">One Cricket Community.</span>
          </h1>

          <p className="hero__subtitle">
            Follow live ball-by-ball cricket, discover player analytics, manage your club,
            and organize league tournaments — all from the web or on your phone.
          </p>

          <div className="hero__actions">
            <Link to="/live-scores" className="btn btn--primary btn--lg btn--pill hero__btn-glow">
              <span className="hero__btn-live-dot" />
              <span>Explore Live Scores</span>
              <ArrowRight size={18} />
            </Link>
            <Link to="/register" className="btn btn--outline btn--lg btn--pill">
              <Star size={18} />
              <span>Join Free</span>
            </Link>
          </div>

          {/* Value Badges */}
          <div className="hero__badges">
            <div className="hero__badge-item">
              <ShieldCheck size={16} className="text-cyan" />
              <span>Verified Scorers</span>
            </div>
            <div className="hero__badge-item">
              <Zap size={16} className="text-cyan" />
              <span>Instant Scorecard Sync</span>
            </div>
            <div className="hero__badge-item">
              <Trophy size={16} className="text-cyan" />
              <span>Tournament Leaderboards</span>
            </div>
          </div>

          {/* Live match count indicator */}
          {liveMatches.length > 0 && (
            <div className="hero__live-indicator">
              <span className="hero__pulse-dot hero__pulse-dot--red" />
              <span>{liveMatches.length} match{liveMatches.length > 1 ? 'es' : ''} live now</span>
              <Link to="/live-scores">Watch →</Link>
            </div>
          )}
        </div>

        {/* Right Column: Live Scorecard or Showcase */}
        <div className="hero__visual" aria-label="Interactive Live Match Preview">
          {currentMatch ? (
            <HeroLiveScorecard match={currentMatch} />
          ) : (
            <HeroShowcase />
          )}
        </div>
      </div>
    </section>
  );
}
